/** @jsxImportSource @emotion/react */

import { ApolloError, useMutation } from '@apollo/client'
import { IconButton, Modal } from '@mui/material'
import Typography from '@mui/material/Typography'
import { FC, useCallback, useEffect, useState } from 'react'
import { last } from 'lodash'
import { findAllRecommendedArticleGroup_findAllRecommendedArticleGroup_items as Group } from '__generatedTypes__/findAllRecommendedArticleGroup'

import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import DeleteModal from 'components/Modals/DeleteModal'
import Table, { TableProps } from 'components/Table'
import TableButtons from 'components/TableButtons/TableButtons'
import { ClassroomTabLiveSwitcher } from 'components/ClassroomTabLiveSwitcher'
import { SubjectExtendedFull } from 'hooks/usePlatformsAndSubjects'
import client from 'configs/apolloClient'
import { GROUP_COLORS } from 'pagesNew/FlashCards/consts'
import * as commonClasses from 'components/shared/GroupCards/commonStyles'
import * as classes from 'pagesNew/FlashCards/FlashCardsGroups/styles'
import { GroupIconsAWS } from 'constants/consts'
import { ClassroomTabEnum, GroupIcon } from '__generatedTypes__/globalTypes'
import Alert from 'components/Alert'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'
import {
  BULK_REMOVE_RECOMMENDED_ARTICLE_GROUP,
  BULK_UPDATE_RECOMMENDED_ARTICLE_GROUP,
  FIND_ALL_RECOMMENDED_ARTICLE_GROUP,
} from 'queries/recommendedArticleGroup'
import {
  bulkUpdateRecommendedArticleGroup,
  bulkUpdateRecommendedArticleGroupVariables,
} from '__generatedTypes__/bulkUpdateRecommendedArticleGroup'
import {
  BulkRemoveRecommendedArticleGroup,
  BulkRemoveRecommendedArticleGroupVariables,
} from '__generatedTypes__/BulkRemoveRecommendedArticleGroup'
import { toast } from 'utils/toast'
import { ManageRecommendedArticlesGroupsModal } from './ManageRecommendedArticlesGroupsModal'
import * as classesStyles from './styles'

type Props = {
  groups: Group[]
  selectedSubject: SubjectExtendedFull
  setGroups: (groups: Group[]) => void
}

const RecommendedArticlesGroups: FC<Props> = ({ groups, selectedSubject, setGroups }) => {
  const [isGroupModalOpen, setGroupModalOpen] = useState<boolean>(false)
  const [isBulkDeleteModalOpen, setBulkDeleteModalOpen] = useState<boolean>(false)
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null)
  const [availableGroupColors, setAvailableGroupColors] = useState<string[]>([])
  const [deleteGroup, setDeleteGroup] = useState<Group[]>([])
  const [alertMessage, setAlertMessage] = useState('')

  const [bulkUpdateRecommendedArticleGroup] = useMutation<
    bulkUpdateRecommendedArticleGroup,
    bulkUpdateRecommendedArticleGroupVariables
  >(BULK_UPDATE_RECOMMENDED_ARTICLE_GROUP)
  const [bulkRemoveRecommendedArticleGroup] = useMutation<
    BulkRemoveRecommendedArticleGroup,
    BulkRemoveRecommendedArticleGroupVariables
  >(BULK_REMOVE_RECOMMENDED_ARTICLE_GROUP)

  const handleBulkDelete = useCallback(async () => {
    const ids = deleteGroup.map((group) => ({ id: group.id }))

    try {
      await bulkRemoveRecommendedArticleGroup({ variables: { ids } })
      setDeleteGroup([])
      client.refetchQueries({
        include: [FIND_ALL_RECOMMENDED_ARTICLE_GROUP],
      })

      toast.success('Groups deleted successfully')
    } catch (error) {
      if (error instanceof Error) {
        if ('graphQLErrors' in error) {
          const gQLErr = error.graphQLErrors as ApolloError['graphQLErrors']
          setAlertMessage(gQLErr[0]?.extensions?.response?.message || '')
        } else {
          toast.error('Failed to delete groups')
        }
      } else {
        toast.error('Failed to delete groups')
      }
    } finally {
      setBulkDeleteModalOpen(false)
    }
  }, [deleteGroup])

  const handleEditClick = (group: Group) => {
    setSelectedGroup(group)
    setGroupModalOpen(true)
    setDeleteGroup([])
  }

  const handleModalClose = () => {
    setSelectedGroup(null)
    setGroupModalOpen(false)
  }

  useEffect(() => {
    const groupColors = groups.map((group) => group.color)
    setAvailableGroupColors(GROUP_COLORS.filter((color) => !groupColors.includes(color)))
  }, [groups, selectedSubject.id])

  useEffect(() => {
    setDeleteGroup([])
  }, [selectedSubject.id])

  const handleSort = useCallback(async (items: Group[]) => {
    try {
      if (items.length > 0) {
        setGroups(items)
        await bulkUpdateRecommendedArticleGroup({
          variables: {
            bulkUpdateRecommendedArticleGroupInput: items.map(({ id }, order) => ({ id, order })),
          },
        })
        toast.success('Groups order updated successfully')
      }
    } catch (error) {
      toast.error('Failed to update groups order')
    }
  }, [])

  const GroupsColumns: TableProps<Group>['columns'] = [
    {
      title: 'Group Title',
      renderCell: (group) => (
        <div css={classes.groupTitle}>
          <div css={classesStyles.groupIconWrapper}>
            <img
              src={GroupIconsAWS[group.icon as GroupIcon]}
              alt="Group Icon"
              css={[classes.icon, classesStyles.groupIcon]}
            />
          </div>
          <div css={classes.circle(group.color)} />
          <Typography css={commonClasses.longText} variant="body2" data-testid="group-title">
            {group.title}
          </Typography>
        </div>
      ),
      flex: '0 1 484px',
    },
    {
      title: 'Description',
      renderCell: (group) => (
        <Typography css={commonClasses.longText} variant="body2">
          {group.description}
        </Typography>
      ),
      flex: '0 1 484px',
    },
    {
      renderCell: (group) => (
        <EditIcon css={commonClasses.editIcon} onClick={() => handleEditClick(group)} data-testid="edit-group-btn" />
      ),
      flex: '0 1 29px',
    },
  ]

  const lastGroupOrder = last(groups)?.order

  return (
    <div>
      <div css={classes.header}>
        <Typography css={classes.title} variant="title3" component="p">
          Group
        </Typography>
        <ClassroomTabLiveSwitcher selectedSubject={selectedSubject} tabEnum={ClassroomTabEnum.RecommendedArticles} />
      </div>
      {alertMessage && (
        <Alert
          severity="error"
          message={alertMessage}
          icon={false}
          action={
            <IconButton color="inherit" size="small" onClick={() => setAlertMessage('')}>
              <CrossIcon fontSize="inherit" />
            </IconButton>
          }
        />
      )}
      <Table
        css={classesStyles.table}
        columns={GroupsColumns}
        items={groups}
        isSelectable
        selectedItems={deleteGroup}
        onSelect={setDeleteGroup}
        onSort={handleSort}
        isSortable
      />
      <TableButtons
        itemName="Group"
        areItemsToDelete={deleteGroup.length !== 0}
        onAddItem={() => setGroupModalOpen(true)}
        onCancel={() => setDeleteGroup([])}
        onBatchDelete={() => setBulkDeleteModalOpen(true)}
      />
      <Modal open={isGroupModalOpen} onClose={handleModalClose}>
        <ManageRecommendedArticlesGroupsModal
          groupOrder={lastGroupOrder !== undefined ? lastGroupOrder + 1 : 0}
          availableGroupColors={availableGroupColors}
          handleClose={handleModalClose}
          group={selectedGroup}
          selectedSubjectId={selectedSubject.id}
        />
      </Modal>
      <DeleteModal
        open={isBulkDeleteModalOpen}
        count={deleteGroup.length}
        onClose={() => setBulkDeleteModalOpen(false)}
        onConfirm={handleBulkDelete}
      />
    </div>
  )
}

export default RecommendedArticlesGroups
